/**
 * Generic "linked dropdown" resolver.
 *
 * A dropdown field is treated as a dependent (child) of another dropdown
 * (its controller/parent) when its field_items carry a non-null `parent`
 * value. The controller is resolved by:
 *   1. Matching the dependent's field-level `parent` string against a
 *      sibling dropdown's field_key/field_label (normalized), e.g.
 *      parent: "casualty type" -> field_key: "casualty_type".
 *   2. Falling back to matching the set of item-level `parent` values
 *      against a sibling dropdown's item names (e.g. parent: "linking"
 *      matches a sibling item named "linking").
 * If no single unambiguous controller is found, the field is left as an
 * ordinary, unfiltered dropdown.
 */

const normalizeKey = (str) =>
  (str || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");

const getDistinctItemParents = (field) => {
  if (!Array.isArray(field.field_items)) return [];
  const parents = field.field_items
    .filter((item) => typeof item === "object" && item?.parent != null && item.parent !== "")
    .map((item) => item.parent);
  return Array.from(new Set(parents));
};

const findControllerByFieldParent = (field, dropdownFields) => {
  const key = normalizeKey(field.parent);
  if (!key) return null;
  const matches = dropdownFields.filter(
    (candidate) =>
      candidate !== field &&
      (normalizeKey(candidate.field_key) === key ||
        normalizeKey(candidate.field_label) === key)
  );
  return matches.length === 1 ? matches[0] : null;
};

const findControllerByItemOverlap = (field, dropdownFields, parentValues) => {
  const matches = dropdownFields.filter((candidate) => {
    if (candidate === field || !Array.isArray(candidate.field_items)) return false;
    const names = candidate.field_items
      .filter((item) => typeof item === "object")
      .map((item) => item.name);
    return parentValues.every((value) => names.includes(value));
  });
  return matches.length === 1 ? matches[0] : null;
};

/**
 * Mutates and returns `fields`, tagging linked dropdown pairs with:
 *  - `linkedControllerName` on the dependent field (the controller's field_label)
 *  - `linkedDependentNames` on the controller field (array of dependents' field_label)
 * @param {Array<Object>} fields
 */
export const annotateLinkedDropdowns = (fields) => {
  if (!Array.isArray(fields)) return fields;

  const dropdownFields = fields.filter((f) => f?.field_type?.includes("dropdown"));

  dropdownFields.forEach((field) => {
    const parentValues = getDistinctItemParents(field);
    if (parentValues.length === 0) return;

    const controller =
      findControllerByFieldParent(field, dropdownFields) ||
      findControllerByItemOverlap(field, dropdownFields, parentValues);

    if (!controller) return;

    field.linkedControllerName = controller.field_label;
    controller.linkedDependentNames = [
      ...(controller.linkedDependentNames || []),
      field.field_label,
    ];
  });

  return fields;
};

/**
 * Resolves item-level `triggers: [{ action: "reveal_field", payload: { field_labels } }]`
 * into a `revealedBy` list on each target field:
 *   revealedBy: [{ sourceFieldName: "<runtime field name>", value: "<item name that reveals it>" }]
 * A field with a non-empty `revealedBy` starts hidden and only renders once one of
 * its listed source fields currently holds the matching value (OR semantics across
 * entries, so multiple options/fields can each independently reveal the same target).
 * `payload.field_labels` is an array so a single option can reveal more than one
 * field; the legacy singular `payload.field_label` (one target) is still honored
 * for triggers saved before multi-field support existed.
 *
 * Must run against the RAW (pre-transformData) field_label of both source and target,
 * since transformData overwrites field_label with field_key before this ever sees it.
 * `rawFields` and `mappedFields` must be index-aligned (same order, same length).
 *
 * @param {Array<Object>} rawFields - original, untransformed form_fields
 * @param {Array<Object>} mappedFields - same fields after transformData's field_label mutation
 */
export const resolveFieldTriggers = (rawFields, mappedFields) => {
  if (!Array.isArray(rawFields) || !Array.isArray(mappedFields)) return mappedFields;

  // When several fields share the same label/key (e.g. a form reusing
  // "Attach File" under every yes/no dropdown), the nearest occurrence to
  // the triggering field wins - forms are authored with the target placed
  // directly beneath the dropdown that reveals it, so position disambiguates
  // what the label alone cannot. Ties/preference go to the closest match
  // that comes after the source field.
  const nearestMatch = (matches, fromIndex) => {
    if (matches.length <= 1) return matches[0] ?? -1;
    const after = matches.filter((i) => i > fromIndex);
    if (after.length) return after[0];
    return matches.reduce((closest, i) =>
      Math.abs(i - fromIndex) < Math.abs(closest - fromIndex) ? i : closest
    );
  };

  const findTargetIndex = (labelOrKey, fromIndex) => {
    if (!labelOrKey) return -1;
    const exactMatches = rawFields
      .map((_, i) => i)
      .filter(
        (i) => rawFields[i].field_label === labelOrKey || rawFields[i].field_key === labelOrKey
      );
    if (exactMatches.length) return nearestMatch(exactMatches, fromIndex);

    const key = normalizeKey(labelOrKey);
    const matches = rawFields
      .map((_, i) => i)
      .filter(
        (i) =>
          normalizeKey(rawFields[i].field_label) === key ||
          normalizeKey(rawFields[i].field_key) === key
      );
    return matches.length === 1 ? matches[0] : -1;
  };

  rawFields.forEach((rawField, index) => {
    if (!Array.isArray(rawField.field_items)) return;

    rawField.field_items.forEach((item) => {
      if (typeof item !== "object" || !Array.isArray(item.triggers)) return;

      item.triggers.forEach((trigger) => {
        if (trigger?.action !== "reveal_field") return;
        const labels = Array.isArray(trigger.payload?.field_labels)
          ? trigger.payload.field_labels
          : (trigger.payload?.field_label ? [trigger.payload.field_label] : []);

        labels.forEach((label) => {
          const targetIndex = findTargetIndex(label, index);
          if (targetIndex === -1) return;

          const targetMapped = mappedFields[targetIndex];
          if (!targetMapped) return;

          targetMapped.revealedBy = [
            ...(targetMapped.revealedBy || []),
            { sourceFieldName: mappedFields[index].field_label, value: item.name },
          ];
        });
      });
    });
  });

  return mappedFields;
};

/**
 * Whether a field tagged with `revealedBy` (see resolveFieldTriggers) is
 * currently visible given the form's live values. Fields with no
 * `revealedBy` are always visible. Shared by Field (render-time) and
 * applyVisibilityToSchema (validate-time) so the two never drift apart.
 * @param {Object} field
 * @param {Object} values - current formik values
 */
export const isFieldVisible = (field, values) => {
  const revealedBy = field?.revealedBy;
  if (!revealedBy?.length) return true;
  return revealedBy.some((r) => values?.[r.sourceFieldName] === r.value);
};

/**
 * A required field that's currently hidden (its `revealedBy` condition
 * isn't met) must not block submission - the user has no way to fill in
 * a field they can't see. Returns a schema with `required` disabled for
 * every currently-hidden field, only cloning `schema` if a change is
 * actually needed.
 * @param {Array<Object>} fields - annotated fields (same ones passed to Form/getInitialValues3)
 * @param {Object} schema - static schema from makeSchemaV3
 * @param {Object} values - current formik values
 */
export const applyVisibilityToSchema = (fields, schema, values) => {
  if (!Array.isArray(fields) || !schema) return schema;

  let visibleSchema = schema;
  fields.forEach((field) => {
    if (!field?.revealedBy?.length || isFieldVisible(field, values)) return;

    const key = field.field_label;
    if (visibleSchema[key]?.required) {
      if (visibleSchema === schema) visibleSchema = { ...schema };
      visibleSchema[key] = { ...visibleSchema[key], required: false };
    }
  });
  return visibleSchema;
};
